/**
 * "PPAs & Contracts" workspace tab strip.
 *
 * Renders at the top of every page in this section so users can move across
 * sub-pages without leaving the section. URL-driven so deep links work.
 *
 * Tabs:
 *   Cards          → /portfolios/:id/ppas
 *   Stack          → /portfolios/:id/ppas/stack
 *   Reconciliation → /portfolios/:id/ppas/reconcile
 *
 * Calendar moved out of this section — it now lives under the Portfolio
 * (Overview) sidebar section at /portfolios/:id/calendar.
 * Documents now lives as its own top-level sidebar section
 * (/portfolios/:id/contracts), not as a tab here. AI Assistant moved to
 * the global assistant — no longer scoped to this section.
 */

import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Tabs, Tab, Box } from '@mui/material';

// Tab labels reflect use-case, not visual metaphor:
//   Term Sheet  — read one contract's key terms (was: Cards)
//   Revenue Mix — see how contracts combine on assets / portfolio (was: Stack)
// Route paths kept stable so existing bookmarks still resolve; only labels change.
const TABS = [
  { value: 'cards',      label: 'Term Sheet',     path: 'ppas' },
  { value: 'fair-value', label: 'Fair Value',     path: 'ppas/fair-value' },
  { value: 'stack',      label: 'Revenue Mix',    path: 'ppas/stack' },
  { value: 'reconcile',  label: 'Reconciliation', path: 'ppas/reconcile' },
  { value: 'invoices',   label: 'Invoices',       path: 'ppas/invoices' },
];

function resolveActiveTab(pathname) {
  if (pathname.endsWith('/ppas/fair-value')) return 'fair-value';
  if (pathname.endsWith('/ppas/stack'))      return 'stack';
  if (pathname.endsWith('/ppas/reconcile'))  return 'reconcile';
  if (pathname.endsWith('/ppas/invoices'))   return 'invoices';
  return 'cards';   // /ppas default
}

export default function PpaWorkspaceTabs() {
  const { portfolioId } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const active = resolveActiveTab(pathname);

  return (
    <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', mb: 2 }}>
      <Tabs
        value={active}
        onChange={(_, v) => {
          const t = TABS.find((x) => x.value === v);
          if (t) navigate(`/portfolios/${portfolioId}/${t.path}`);
        }}
        variant="scrollable"
        scrollButtons="auto"
        TabIndicatorProps={{ sx: { height: 2 } }}
        sx={{ minHeight: 38, '& .MuiTab-root': { minHeight: 38, py: 1 } }}
      >
        {TABS.map((t) => (
          <Tab key={t.value} value={t.value} label={t.label} />
        ))}
      </Tabs>
    </Box>
  );
}
